import FirestoreManager from '../api/firebaseManager';
import createDataContext from './createDataContext';

const rampReducer = (state, action) => {
  const {connection, id, connections, ids, uid, user_id} = action.payload;
  switch (action.type) {
    case 'default':
      return {...state, ...action.payload};
    case 'createConnection':
      return {
        ...state,
        connection_ids: [id, ...state.connection_ids],
        connections: {...state.connections, [id]: connection},
      };
    case 'editConnection':
      return {
        ...state,
        connections: {
          ...state.connections,
          [id]: {...state.connections[id], ...connection},
        },
      };
    case 'getUserConnections':
      return {
        ...state,
        user_connections: {...state.user_connections, [uid]: ids},
        connections: {...state.connections, ...connections},
      };
    case 'removeConnection':
      const removed_connections = [...state.connection_ids].filter(
        con_id => con_id !== id,
      );
      return {...state, connection_ids: removed_connections};
    case 'removeUser':
      const filtered_connections = {...state.user_connections, [user_id]: []};
      return {
        ...state,
        user_connections: filtered_connections,
      };
    case 'error':
      return {...state, error: action.payload};
    default:
      return state;
  }
};

const fetchInitialRamp = dispatch => async uid => {
  try {
    const connection_list = await FirestoreManager.getRampItems(uid);

    const connections = {};
    const connection_ids = connection_list.map(item => {
      const {id} = item;
      connections[id] = item;
      return id;
    });

    const payload = {
      connection_ids,
      connections,
      error: null,
    };
    dispatch({type: 'default', payload});
  } catch (err) {
    dispatch({type: 'error', payload: 'Something went wrong'});
  }
};

// Creates the Ramp accounting connection via the backend (which registers it
// with Ramp AND writes the Firestore doc), then mirrors it into local state.
const createConnection = dispatch => async ({customerID, entityName, uid}) => {
  try {
    const response = await FirestoreManager.createRampConnection({
      customerID,
      entityName,
      uid,
    });

    if (!response?.success) {
      return {
        success: false,
        error: response?.error || 'Failed to create Ramp connection',
      };
    }

    const connection_id = response.data?.connection_id;
    const connection = {
      id: connection_id,
      customerID,
      entityName,
      uid,
      status: response.data?.status || 'linked',
    };

    dispatch({
      type: 'createConnection',
      payload: {id: connection_id, connection},
    });
    return {success: true, connection_id, error: null};
  } catch (err) {
    console.log(err);
    dispatch({type: 'error', payload: 'Something went wrong'});
    return {success: false, error: err?.message || err};
  }
};

const getUserConnections = dispatch => async uid => {
  try {
    const connection_list = await FirestoreManager.getRampItems(uid);

    const connections = {};
    const ids = connection_list.map(item => {
      const {id} = item;
      connections[id] = item;
      return id;
    });

    const payload = {ids, connections, uid};
    dispatch({type: 'getUserConnections', payload});
  } catch (err) {
    dispatch({type: 'error', payload: 'Something went wrong'});
  }
};

// Disconnects the connection: unlinks it inside Ramp AND removes it from
// Connect (backend ramp-disconnect does both). Returns a warning if the Ramp
// side couldn't be unlinked but the local record was still removed.
const removeConnection = dispatch => async connection_id => {
  try {
    const result = await FirestoreManager.disconnectRamp(connection_id);
    if (result?.success === false) {
      dispatch({type: 'error', payload: result.error || 'Something went wrong'});
      return {success: false, error: result.error};
    }
    const payload = {id: connection_id};
    dispatch({type: 'removeConnection', payload});
    return {
      success: true,
      rampUnlinked: result?.rampUnlinked,
      warning: result?.warning,
      error: null,
    };
  } catch (err) {
    dispatch({type: 'error', payload: 'Something went wrong'});
    return {success: false, error: err};
  }
};

const removeUserConnections = dispatch => async (user_id, connection_ids) => {
  try {
    await Promise.all(
      (connection_ids || []).map(async connection_id => {
        await FirestoreManager.removeRampItem(connection_id);
      }),
    );
    const payload = {user_id};
    dispatch({type: 'removeUser', payload});
  } catch (err) {
    dispatch({type: 'error', payload: 'Something went wrong'});
  }
};

const defaultValues = {
  connection_ids: [],
  connections: {},
  user_connections: [],
  error: null,
};

export const {Provider, Context} = createDataContext(
  rampReducer,
  {
    fetchInitialRamp,
    createConnection,
    getUserConnections,
    removeConnection,
    removeUserConnections,
  },
  defaultValues,
);
